import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Divider,
  CircularProgress
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AddRoleModal from "./AddRoleModal";
import EditRoleModal from "./EditRoleModal";
import ViewRoleModal from "./ViewRoleModal";
import { useDispatch} from "react-redux";
import { GetApiPermission, PortalBasedRoles} from '../../redux/Actions/userStatusService';
import '../../Styles/setting.css';

function RoleManagementComponent() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [openAddRoleModal, setOpenAddRoleModal] = useState(false);
  const [openEditRoleModal, setOpenEditRoleModal] = useState(false);
  const [openViewRoleModal, setOpenViewRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const type=localStorage.getItem("type")
  // Map tab index to role type
  const tabToRoleType = {
    0: "INVESTOR",
    1: "MANAGER",
    2: "ADMIN"
  };

  // Get current role type based on active tab
  const currentRoleType = tabToRoleType[activeTab];

  // Fetch roles when tab changes
  useEffect(() => {
    fetchRoles(currentRoleType);
  }, [currentRoleType]);

  // Function to fetch roles based on role type
  const fetchRoles = async (roleType) => {
    setLoading(true);
    try {
      let rolesData = [];
      // For Admin tab, only fetch SUPER_ADMIN
      if (roleType === 'ADMIN') {
        const superAdminResponse = await dispatch(PortalBasedRoles({type, roleType: 'SUPER_ADMIN'})).unwrap();
        rolesData = Array.isArray(superAdminResponse.data) ? superAdminResponse.data : [];
      } else {
        const response = await dispatch(PortalBasedRoles({type, roleType})).unwrap();
        rolesData = Array.isArray(response.data) ? response.data : [];
      }
      // Use groupName for display in the list
      const formattedRoles = rolesData.map((role, index) => ({
        id: role.id || index + 1,
        name: role.groupName || role.groupRole || `Role ${index + 1}`,
        description: role.description || "",
        permissions: role.apiNames || [],
        originalData: role
      }));
      setRoles(formattedRoles);
    } catch (error) {
      // Error fetching group roles
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch permissions for add/edit modals
  const fetchPermissions = async (roleType, groupRole = null) => {
    setLoadingPermissions(true);
    try {
      const params = { type: type, groupRole: roleType };
      
      const response = await dispatch(GetApiPermission(params)).unwrap();
     
      if (response && response.data) {
        // Store the permissions
        setPermissions(response.data);
      }
    } catch (error) {
      // Error fetching permissions
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenAddRoleModal = () => {
    // Fetch permissions before opening the modal
    fetchPermissions(currentRoleType);
    setOpenAddRoleModal(true);
  };

  const handleCloseAddRoleModal = () => {
    setOpenAddRoleModal(false);
  };

  const handleAddRole = (newRole) => {
    // Here you would typically make an API call to add the role
    // For now, we'll just update the local state
    setRoles([...roles, { 
      name: newRole.name, 
      id: roles.length + 1,
      permissions: newRole.permissions 
    }]);
    
    // Refresh the roles list after adding
    fetchRoles(currentRoleType);
  };

  const handleOpenEditRoleModal = (role) => {
    // Fetch permissions before opening the edit modal
    fetchPermissions(currentRoleType, role.name);
    // Make sure role has all required fields for edit modal
    const roleToEdit = {
      ...role,
      name: role.originalData?.groupName || role.groupName || role.name, // Prefer groupName
      permissions: role.permissions || []
    };
    setSelectedRole(roleToEdit);
    setOpenEditRoleModal(true);
  };

  const handleCloseEditRoleModal = () => {
    setOpenEditRoleModal(false);
    setSelectedRole(null);
  };

  const handleOpenViewRoleModal = (role) => {
    // Make sure role has all the data needed for the view modal
    const roleToView = {
      ...role,
      name: role.originalData?.groupName || role.groupName || role.name, // Prefer groupName
      permissions: role.permissions || []
    };
    setSelectedRole(roleToView);
    setOpenViewRoleModal(true);
  };

  const handleCloseViewRoleModal = () => {
    setOpenViewRoleModal(false);
    setSelectedRole(null);
  };

  const handleUpdateRole = (updatedRole) => {
    // Here you would typically make an API call to update the role
    // For now, we'll just update the local state
    const updatedRoles = roles.map(role => 
      role.id === updatedRole.id ? updatedRole : role
    );
    setRoles(updatedRoles);
    
    // Refresh the roles list after updating
    fetchRoles(currentRoleType);
  };


  return (
    <Paper elevation={1} sx={{ p: 3, minHeight: '500px' }} className="role-management-container">
      <Typography 
        variant="h6" 
        className="role-management-heading"
        sx={{ mb: 2 }}
      >
        Role Management
      </Typography>
      <Typography 
        variant="body2" 
        className="role-management-description"
        sx={{ mb: 3 }}
      >
        Add and manage roles and their permissions
      </Typography>

      <Box className="role-management-tabs">
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          TabIndicatorProps={{
            className: "role-management-tab-indicator"
          }}
          sx={{
            '& .MuiTab-root': {
              className: "role-management-tab"
            }
          }}
        >
          <Tab 
            label="Investor" 
            className="role-management-tab"
          />
          <Tab 
            label="Manager" 
            className="role-management-tab"
          />
          <Tab 
            label="Admin" 
            className="role-management-tab"
          />
        </Tabs>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <List sx={{ width: '100%' }}>
            {roles.length > 0 ? (
              roles.map((role) => (
                <React.Fragment key={role.id}>
                  <ListItem
                    secondaryAction={
                      <Box>
                        <IconButton 
                          edge="end" 
                          aria-label="view" 
                          sx={{ mr: 1 }}
                          onClick={() => handleOpenViewRoleModal(role)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton 
                          edge="end" 
                          aria-label="edit"
                          onClick={() => handleOpenEditRoleModal(role)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Box>
                    }
                    sx={{ 
                      py: 2,
                      alignItems: 'flex-start', // Align items to top for better layout
                      minHeight: '80px', // Ensure minimum height for consistency
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        borderRadius: 1
                      }
                    }}
                  >
                    <ListItemText 
                      primary={
                        <Typography 
                          className="role-name-typography"
                          sx={{
                            color: 'var(--text-text-neutral-1, #141218)',
                            fontFamily: '"Founders Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                            fontSize: '16px',
                            fontWeight: 500,
                            lineHeight: '24px',
                            letterSpacing: '0.15px',
                            marginBottom: '4px'
                          }}
                        >
                          {role.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ 
                          maxWidth: 'calc(100% - 120px)', // Reserve space for action buttons
                          wordBreak: 'break-word',
                          overflow: 'hidden'
                        }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: 1.4,
                              maxHeight: '2.8em' // 2 lines * 1.4 line height
                            }}
                            title={role.description} // Show full description on hover
                          >
                            {role.description }
                          </Typography>
                        </Box>
                      }
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: 'var(--text-text-neutral-1, #141218)',
                          fontFamily: '"Founders Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                          fontSize: '16px',
                          fontWeight: 500,
                          lineHeight: '24px',
                          letterSpacing: '0.15px',
                          marginBottom: '4px'
                        },
                        '& .MuiListItemText-secondary': {
                          mt: 0.5
                        }
                      }}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                No roles found for this category
              </Typography>
            )}
          </List>

          <Button 
            className="add-role-button"
            onClick={handleOpenAddRoleModal}
          >
            Add Role
          </Button>
        </>
      )}

      <AddRoleModal 
        open={openAddRoleModal} 
        handleClose={handleCloseAddRoleModal} 
        handleAddRole={(newRole) => {
          // Refresh the roles list after successful addition
          fetchRoles(currentRoleType);
        }}
        permissions={permissions}
        loading={loadingPermissions}
        currentRoleType={currentRoleType}
      />

      <EditRoleModal 
        open={openEditRoleModal}
        handleClose={handleCloseEditRoleModal}
        handleUpdateRole={handleUpdateRole}
        selectedRole={selectedRole}
        permissions={permissions}
        loading={loadingPermissions}
      />

      <ViewRoleModal 
        open={openViewRoleModal}
        handleClose={handleCloseViewRoleModal}
        selectedRole={selectedRole}
        handleOpenEditModal={handleOpenEditRoleModal}
      />
    </Paper>
  );
}

export default RoleManagementComponent; 